import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router";
import { useAuth } from "../context/AuthContext";
import HamburgerMenu from "./HamburgerMenu";
import ToolSwitcher from "./ToolSwitcher";
import { STATS_NAV_ITEM, TOOL_NAV_ITEMS } from "../constants/navigation";
import "./Header.css";

function Header() {
  const { isAuthenticated, isLoading } = useAuth();
  const { pathname } = useLocation();
  const isToolPage = pathname === "/app" || pathname === "/simulator" ||
                     pathname === "/dashboard" || pathname === "/profile";
  const isSitePage = pathname === "/" || pathname === "/about" || pathname === "/newsletter" ||
                     pathname === "/privacy-policy" || pathname === "/terms-of-service" ||
                     pathname === "/cookie-policy" || pathname === "/data-deletion";
  const [landingMenuOpen, setLandingMenuOpen] = useState(false);
  const landingMenuRef = useRef(null);

  // Close landing mobile menu when clicking outside
  useEffect(() => {
    if (!landingMenuOpen) return;
    const handleClickOutside = (event) => {
      if (landingMenuRef.current && !landingMenuRef.current.contains(event.target)) {
        setLandingMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [landingMenuOpen]);

  // Close on route change
  useEffect(() => {
    setLandingMenuOpen(false);
  }, [pathname]);

  const closeLandingMenu = () => setLandingMenuOpen(false);

  return (
    <header className={`header${isSitePage ? ' header--landing' : ''}`} role="banner">
      <Link
        to="/"
        className="header-logo-link"
        aria-label="EQ Your Ears - Home"
      >
        <span className="app-title">EQ Your Ears</span>
      </Link>
      <nav
        className="controls"
        role="navigation"
        aria-label="Primary navigation"
      >
        {isSitePage ? (
          <>
            {/* Desktop inline nav */}
            <ul className="header-site-nav landing-nav-desktop">
              <li>
                <Link to="/about" className="header-site-nav-link">About</Link>
              </li>
              <li>
                <Link to="/newsletter" className="header-site-nav-link">Newsletter</Link>
              </li>
              <li>
                <a
                  href="mailto:contact.tekra@gmail.com"
                  className="header-site-nav-link"
                  aria-label="Contact us via email"
                >
                  Contact
                </a>
              </li>
              <li>
                <Link to="/simulator" className="header-site-nav-link">Simulator</Link>
              </li>
              <li>
                <a href="/login" className="header-site-nav-link">Log in</a>
              </li>
              <li>
                <Link to="/app" className="landing-get-started-btn">Open the Trainer</Link>
              </li>
            </ul>

            {/* Mobile hamburger menu */}
            <div className="landing-mobile-menu" ref={landingMenuRef}>
              <button
                className={`landing-hamburger-btn${landingMenuOpen ? ' open' : ''}`}
                onClick={() => setLandingMenuOpen(!landingMenuOpen)}
                aria-label="Toggle navigation menu"
                aria-expanded={landingMenuOpen}
                aria-controls="landing-mobile-nav"
              >
                <span className="landing-hamburger-line" />
                <span className="landing-hamburger-line" />
                <span className="landing-hamburger-line" />
              </button>
              {landingMenuOpen && (
                <>
                  <div
                    className="landing-menu-overlay"
                    onClick={closeLandingMenu}
                    aria-hidden="true"
                  />
                  <ul
                    id="landing-mobile-nav"
                    className="landing-menu-dropdown"
                    role="menu"
                  >
                    <li role="none">
                      <Link to="/about" className="landing-menu-item" role="menuitem" onClick={closeLandingMenu}>
                        About
                      </Link>
                    </li>
                    <li role="none">
                      <Link to="/newsletter" className="landing-menu-item" role="menuitem" onClick={closeLandingMenu}>
                        Newsletter
                      </Link>
                    </li>
                    <li role="none">
                      <a
                        href="mailto:contact.tekra@gmail.com"
                        className="landing-menu-item"
                        role="menuitem"
                        aria-label="Contact us via email"
                        onClick={closeLandingMenu}
                      >
                        Contact
                      </a>
                    </li>
                    <li role="none">
                      <Link to="/simulator" className="landing-menu-item" role="menuitem" onClick={closeLandingMenu}>
                        Simulator
                      </Link>
                    </li>
                    <li role="none">
                      <a href="/login" className="landing-menu-item" role="menuitem" onClick={closeLandingMenu}>
                        Log in
                      </a>
                    </li>
                    <li role="none">
                      <Link to="/app" className="landing-menu-item landing-menu-cta" role="menuitem" onClick={closeLandingMenu}>
                        Open the Trainer
                      </Link>
                    </li>
                  </ul>
                </>
              )}
            </div>
          </>
        ) : (
          <>
            {!isAuthenticated && !isLoading && (
              <ul className="header-site-nav">
                <li>
                  <Link to="/about" className="header-site-nav-link">About</Link>
                </li>
                <li>
                  <Link to="/newsletter" className="header-site-nav-link">Newsletter</Link>
                </li>
                <li>
                  <a
                    href="mailto:contact.tekra@gmail.com"
                    className="header-site-nav-link"
                    aria-label="Contact us via email"
                  >
                    Contact
                  </a>
                </li>
              </ul>
            )}
            {isLoading ? (
              <div className="header-loading" aria-live="polite">
                <div className="header-spinner" role="status">
                  <span className="visually-hidden">
                    Checking authentication...
                  </span>
                </div>
              </div>
            ) : isAuthenticated ? (
              <>
                {isToolPage && (
                  <ToolSwitcher items={[...TOOL_NAV_ITEMS, STATS_NAV_ITEM]} />
                )}
                <HamburgerMenu />
              </>
            ) : isToolPage ? (
              <>
                <ToolSwitcher />
                {/* Using <a> instead of <Link> to force full page load for Lambda@Edge password protection */}
                <a href="/login" className="btn btn-secondary header-nav-link">
                  Sign in
                </a>
                <a href="/signup" className="btn btn-primary header-nav-link">
                  Sign Up
                </a>
              </>
            ) : (
              <Link to="/app" className="landing-get-started-btn">Open the Trainer</Link>
            )}
          </>
        )}
      </nav>
    </header>
  );
}

export default Header;
